import { ref, computed } from 'vue';
import PushNotificationService, { type PushSubscriptionData } from '@/services/pushNotificationService';
import VapidService from '@/services/vapidService';

const pushService = PushNotificationService.getInstance();

// Global reactive state
const isInitialized = ref(false);
const isSupported = ref(false);
const notificationPermission = ref<NotificationPermission>('default');
const subscription = ref<PushSubscriptionData | null>(null);
// Whether the current subscription is known to be registered with our API. The browser
// having a subscription is not enough - if the server row is missing, no reminder is
// ever sent, and that failure is completely invisible without tracking this separately.
const isRegisteredWithServer = ref(false);
const isLoading = ref(false);
const error = ref<string | null>(null);
// Shared across concurrent callers. PushNotificationStatus and PushNotificationManager mount
// at the same time, and the isInitialized flag is only set once initialisation finishes, so
// without this both run the whole thing and each posts the subscription.
let initPromise: Promise<void> | null = null;
let ensurePromise: Promise<boolean> | null = null;

function describeError(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    return err.name === 'Error' ? err.message : `${err.name}: ${err.message}`;
  }
  return fallback;
}

export function usePushNotifications() {
  const canUsePushNotifications = computed(() =>
    isSupported.value && notificationPermission.value === 'granted'
  );

  const isSubscribed = computed(() => subscription.value !== null);

  // Only report notifications as working when both halves agree.
  const isActive = computed(() => isSubscribed.value && isRegisteredWithServer.value);

  /**
   * Pushes the subscription to our API. Called on every startup as well as after a fresh
   * subscribe, because the server row can go missing while the browser subscription
   * survives (failed POST, deleted row, cleanup job) and nothing else would repair it.
   */
  async function syncSubscriptionToServer(sub: PushSubscriptionData): Promise<boolean> {
    try {
      await pushService.saveSubscriptionToServer(sub);
      isRegisteredWithServer.value = true;
      return true;
    } catch (err) {
      isRegisteredWithServer.value = false;
      error.value = 'Notifications are enabled on this device but could not be registered with the server.';
      console.error('Failed to register push subscription with the server:', err);
      return false;
    }
  }

  async function initialize() {
    if (isInitialized.value) return;
    initPromise ??= runInitialize();
    await initPromise;
  }

  async function runInitialize() {
    isLoading.value = true;
    error.value = null;

    try {
      const supported = await pushService.initialize();
      isSupported.value = supported;

      if (supported) {
        notificationPermission.value = Notification.permission;
        // Check for existing subscription
        const existingSubscription = await pushService.getExistingSubscription();
        subscription.value = existingSubscription;

        if (existingSubscription) {
          await syncSubscriptionToServer(existingSubscription);
        }
      }

      isInitialized.value = true;
    } catch (err) {
      error.value = describeError(err, 'Failed to initialize push notifications');
      console.error('Push notification initialization failed:', err);
    } finally {
      isLoading.value = false;
    }
  }

  async function requestPermission(): Promise<boolean> {
    if (!isSupported.value) {
      error.value = 'Push notifications are not supported in this browser';
      return false;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const permission = await pushService.requestNotificationPermission();
      notificationPermission.value = permission;
      if (permission === 'denied') {
        error.value = 'Notifications are blocked for this site. Enable them in your browser settings.';
      }
      return permission === 'granted';
    } catch (err) {
      error.value = describeError(err, 'Failed to request notification permission');
      console.error('Permission request failed:', err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function subscribeToPush(vapidPublicKey: string): Promise<PushSubscriptionData | null> {
    if (!canUsePushNotifications.value) {
      error.value = 'Cannot subscribe: notifications not supported or permission denied';
      return null;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const newSubscription = await pushService.subscribeToPush(vapidPublicKey);
      subscription.value = newSubscription;
      return newSubscription;
    } catch (err) {
      error.value = describeError(err, 'Failed to subscribe to push notifications');
      console.error('Push subscription failed:', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * The single entry point for turning notifications on: permission, browser
   * subscription and server registration. Both the status widget and the meeting
   * banner call this so the two paths cannot drift apart.
   */
  async function enableNotifications(): Promise<boolean> {
    error.value = null;

    let vapidPublicKey: string;
    try {
      vapidPublicKey = await VapidService.getInstance().getVapidPublicKey();
    } catch (err) {
      error.value = 'Could not load the notification key from the server. Try again shortly.';
      console.error('Failed to get VAPID public key:', err);
      return false;
    }

    if (!(await requestPermission())) {
      return false;
    }

    const newSubscription = await subscribeToPush(vapidPublicKey);
    if (!newSubscription) {
      return false;
    }

    return await syncSubscriptionToServer(newSubscription);
  }

  /**
   * Re-enables notifications without any user interaction when permission has already
   * been granted but the subscription is gone. Previously this state required a manual
   * tap every single time, which is what made the app feel like it kept forgetting.
   */
  async function ensureSubscribed(): Promise<void> {
    if (!isInitialized.value || !isSupported.value) return;
    if (notificationPermission.value !== 'granted') return;
    if (isActive.value) return;

    // Same concurrency concern as initialize() - two components call this on mount and
    // must not each drive a separate subscribe.
    ensurePromise ??= enableNotifications().finally(() => { ensurePromise = null; });
    await ensurePromise;
  }

  async function unsubscribe(): Promise<boolean> {
    if (!subscription.value) {
      return true; // Already unsubscribed
    }

    isLoading.value = true;
    error.value = null;

    try {
      const success = await pushService.unsubscribeFromPush();
      if (success) {
        subscription.value = null;
        isRegisteredWithServer.value = false;
      }
      return success;
    } catch (err) {
      error.value = describeError(err, 'Failed to unsubscribe from push notifications');
      console.error('Push unsubscription failed:', err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // Show a test notification (for testing purposes)
  function showTestNotification() {
    if (notificationPermission.value !== 'granted') {
      error.value = 'Notification permission not granted';
      return;
    }

    new Notification('Test Notification', {
      body: 'This is a test notification from Zoom Scheduler',
      icon: '/favicon.ico',
      badge: '/favicon.ico'
    });
  }

  return {
    // State
    isInitialized: computed(() => isInitialized.value),
    isSupported: computed(() => isSupported.value),
    notificationPermission: computed(() => notificationPermission.value),
    subscription: computed(() => subscription.value),
    isRegisteredWithServer: computed(() => isRegisteredWithServer.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    canUsePushNotifications,
    isSubscribed,
    isActive,

    // Actions
    initialize,
    requestPermission,
    subscribeToPush,
    enableNotifications,
    ensureSubscribed,
    unsubscribe,
    showTestNotification,

    // Utility
    clearError: () => { error.value = null; }
  };
}
